import { useQuery } from '@apollo/client';
import { AddCircle, CreateNewFolder, OndemandVideo, Sell } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMovePlaylist } from '~/api/playlists';
import { FilterSidebar, FilterToggle } from '~/components/Filter';
import { ListPagination } from '~/components/ListPagination';
import {
  ListActions,
  ListContainer,
  ListContent,
  ListHeader,
  ListPageContainer,
} from '~/components/PageLayout';
import { ViewToggle } from '~/components/ViewToggle';
import { Count, CountSeparator } from '~/components/count';
import { MoveToFolderDialog } from '~/components/dialogs/MoveToFolderDialog';
import { FolderBreadcrumbs } from '~/components/folder-breadcrumbs';
import { SearchBar } from '~/components/forms/search-bar';
import { Toolbar } from '~/components/toolbar';
import { useAppContext, useFolderContext } from '~/contexts';
import type { PlaylistsOrderBy } from '~/generated/graphql';
import { useOrderByParams } from '~/hooks/order-by';
import { usePaginationParams, useTotalCount } from '~/hooks/pagination';
import { assert } from '~/lib/assert';
import { NewPlaylistDialog } from '../components/new-dialog';
import { PlaylistListDocument } from '../queries/queries.generated';
import {
  AddFolderDialog,
  BulkActions,
  BulkTagDialog,
  FilterAccordion,
  FilterChips,
  PlaylistGrid,
  Table,
  TagsDialog,
} from './components/';
import { PlaylistMoveDocument } from './components/PlaylistMove.generated';
import { useFilters, useSelectedTagParams } from './lib';

export const PlaylistList = () => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const { folderTree } = useFolderContext();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams<{ playlistFolderId: string }>();
  const playlistFolderId = Number(params.playlistFolderId);
  assert(isFinite(playlistFolderId), 'playlistFolderId param is not a legit number');

  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view')?.trim() || 'List';

  const handleSearch = useCallback(
    (search: string) => {
      if (!search) return;
      navigate(`../search?search=${search}&backToFolder=${playlistFolderId}`);
    },
    [navigate, playlistFolderId],
  );

  const { handleOutOfBounds, page, perPage } = usePaginationParams();

  const [openNewFolderDialog, setOpenNewFolderDialog] = useState(false);
  const [tagPlaylistDialogOpen, setTagPlaylistDialogOpen] = useState(false);
  const [movePlaylistDialogOpen, setMovePlaylistDialogOpen] = useState(false);
  const [playlistManageTags, setManageTags] = useState(false);
  const [newPlaylistOpen, setNewPlaylistOpen] = useState(false);

  const [movePlaylist, movePlaylistInfo] = useMovePlaylist();

  const tags = useSelectedTagParams();

  const { orderBy } = useOrderByParams<PlaylistsOrderBy>();

  const { data, loading } = useQuery(PlaylistListDocument, {
    variables: {
      condition: { tags },
      currentNetworkId: currentNetwork.id,
      page,
      perPage,
      playlistFolderId,
      tags,
      ...(orderBy && { orderBy }),
    },
    onCompleted: (data) => handleOutOfBounds(data.network?.playlistFolder.playlists.pageInfo),
  });

  const { data: playlistMoveData } = useQuery(PlaylistMoveDocument, {
    variables: { networkId: currentNetwork.id },
    skip: !movePlaylistDialogOpen,
  });
  const editableFolderIds = useMemo(
    () =>
      playlistMoveData?.network?.playlistFolders
        .filter((x) => x.canUpdate.value)
        .map((x) => x.id) ?? [],
    [playlistMoveData],
  );

  const totalCount = useTotalCount(data?.network?.playlistFolder.playlists.pageInfo.nodesCount);
  const folderCount = useTotalCount(data?.network?.playlistFolder.children.length);

  const playlists = data?.network?.playlistFolder.playlists.nodes ?? [];
  const folders = data?.network?.playlistFolder.children ?? [];

  const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>([]);

  useEffect(() => setSelectedIds([]), [setSelectedIds]);

  const canDestroySelectedPlaylists = selectedIds.every((selectedId) => {
    const playlist = playlists.find((playlist) => playlist.id === selectedId);
    return playlist?.canDestroy.value;
  });

  const playlistTags = useMemo(
    () => data?.network?.playlistTags.map((x) => x.name),
    [data?.network?.playlistTags],
  );
  const filterCounts = data?.network?.playlistFilterInfo;

  const { filter, clearFilter, setFilterItems, toggleAccordion, toggleItem, togglePanel } =
    useFilters(playlistTags);

  const filtersActive = useMemo(() => tags.length > 0, [tags]);

  return (
    <>
      <Toolbar
        titleIcon={<OndemandVideo />}
        titleText={t('pages.playlists.title')}
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={() => setManageTags(!playlistManageTags)}
              startIcon={<Sell />}
              variant="outlined"
            >
              {t('pages.playlists.actions.manage_tags')}
            </Button>
            <Button
              disabled={!data?.network?.playlistFolder.canUpdate.value}
              onClick={() => setOpenNewFolderDialog((prev: boolean) => !prev)}
              startIcon={<CreateNewFolder />}
              variant="outlined"
            >
              {t('pages.playlists.actions.create_folder')}
            </Button>
            <Button
              color="primary"
              disabled={!currentNetwork.canCreatePlaylists.value}
              onClick={() => setNewPlaylistOpen(true)}
              startIcon={<AddCircle />}
              variant="contained"
            >
              {t('pages.playlists.actions.new_playlist')}
            </Button>
          </Box>
        }
        breadcrumbsLabel={
          data?.network?.playlistFolder && (
            <FolderBreadcrumbs
              allItems={() =>
                navigate({ pathname: `../${folderTree.id}`, search: location.search })
              }
              label={t('pages.playlists.all_playlists')}
              changeFolder={(id) => navigate({ pathname: `../${id}`, search: location.search })}
              folder={data.network.playlistFolder}
            />
          )
        }
      />

      <ListPageContainer>
        <FilterSidebar open={filter.panelOpen} persistAs="playlistList:filterWidth">
          <FilterAccordion
            clearFilter={clearFilter}
            filter={filter}
            filterCounts={filterCounts}
            toggleAccordion={toggleAccordion}
            updateFilter={setFilterItems}
          />
        </FilterSidebar>
        <ListContainer>
          <ListHeader>
            <ListActions>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <FilterToggle active={filtersActive} onToggle={togglePanel} />
                <SearchBar
                  onChange={handleSearch}
                  placeholder={t('pages.playlists.search_placeholder')}
                  search=""
                />
              </Box>
              <Box sx={{ display: { md: 'flex', xs: 'none' }, alignItems: 'center' }}>
                {selectedIds.length > 0 ? (
                  <BulkActions
                    selectedIds={selectedIds}
                    onTagPlaylist={() => setTagPlaylistDialogOpen(true)}
                    onMovePlaylist={() => setMovePlaylistDialogOpen(true)}
                    canDestroySelectedPlaylists={canDestroySelectedPlaylists}
                  />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      paddingRight: 1,
                    }}
                  >
                    <Count
                      selectedCount={0}
                      totalCount={folderCount}
                      thing="common.pluralize.folder_plural"
                    />
                    <CountSeparator />
                  </Box>
                )}
                <Box sx={{ display: 'flex' }}>
                  <Count
                    selectedCount={selectedIds.length}
                    totalCount={totalCount || 0}
                    thing="common.pluralize.playlist_plural"
                  />
                </Box>
              </Box>
            </ListActions>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingBottom: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  flexWrap: 'wrap',
                  visibility: filtersActive ? 'visible' : 'hidden',
                }}
              >
                <FilterChips items={filter.items || []} toggleItem={toggleItem} />
              </Box>
              <ViewToggle
                view={view as 'List' | 'Grid'}
                onViewChange={(newView) =>
                  setSearchParams((prev) => ({ ...Object.fromEntries(prev), view: newView }))
                }
              />
            </Box>
          </ListHeader>
          <ListContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {view === 'List' ? (
                  <Table
                    playlists={playlists}
                    folders={folders}
                    loading={loading}
                    onCheck={setSelectedIds}
                    selectedIds={selectedIds}
                  />
                ) : (
                  <PlaylistGrid
                    playlists={playlists}
                    folders={folders}
                    onCheck={setSelectedIds}
                    selectedIds={selectedIds}
                  />
                )}
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <ListPagination totalCount={totalCount} />
              </Box>
            </Box>
          </ListContent>

          <MoveToFolderDialog
            editableFolderIds={editableFolderIds}
            onSubmit={async (targetFolderId) => {
              await movePlaylist({
                refetchQueries: ['PlaylistList'],
                variables: {
                  input: {
                    playlistFolderId: targetFolderId,
                    playlistIds: selectedIds as number[],
                  },
                },
              });
              setSelectedIds([]);
            }}
            loading={movePlaylistInfo.loading}
            onClose={() => setMovePlaylistDialogOpen(false)}
            open={movePlaylistDialogOpen}
            i18nKey="pages.playlists.dialogs.move_playlist.title"
          />

          <AddFolderDialog
            playlistFolderId={playlistFolderId}
            openNewFolderDialog={openNewFolderDialog}
            setOpenNewFolderDialog={setOpenNewFolderDialog}
          />
          {playlistManageTags && <TagsDialog onClose={() => setManageTags(false)} open />}

          {tagPlaylistDialogOpen && (
            <BulkTagDialog
              playlistIds={selectedIds as string[]}
              playlistTags={data?.network?.playlistTags.map((x) => x.name) || []}
              onClose={() => setTagPlaylistDialogOpen(false)}
              onComplete={() => setSelectedIds(() => [])}
              open
              tags={tags}
            />
          )}
        </ListContainer>
      </ListPageContainer>
      {newPlaylistOpen && (
        <NewPlaylistDialog fullWidth onClose={() => setNewPlaylistOpen(false)} open />
      )}
    </>
  );
};
